<template>
  <form @submit.prevent="submit" autocomplete="off">
    <h4>{{ $t("Cloud Key Details") }}</h4>
    <b-row>
      <b-col sm="12" md="12" lg="6">
        <b-form-group
          :label="$t('Log Name')"
          label-for="logname"
          :state="$validateState($v.form.logname)"
        >
          <b-form-input
            autofocus
            data-test-id="modal-cloud-keys-log-name"
            id="logname"
            @blur="setValidationInput('logname', $event.target.value)"
            :state="$validateState($v.form.logname)"
            :placeholder="$t('e.g. IT Visitor')"
          />

          <template #invalid-feedback>
            <p v-if="$v.form.logname.$dirty && !$v.form.logname.minLength">
              {{
                $t("X must be at least X characters long.", {
                  field: $t("Log name"),
                  length: $v.form.logname.$params.minLength.min,
                })
              }}
            </p>
            <p v-else-if="!$v.form.logname.required">
              {{ $t("This is a required field.") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
      <b-col sm="12" md="12" lg="6">
        <b-form-group
          :label="$t('Key')"
          label-for="keyid"
          :state="$validateState($v.form.keyid)"
        >
          <b-form-input
            :state="$validateState($v.form.keyid)"
            data-test-id="modal-cloud-keys-key"
            id="keyid"
            @blur="setValidationInput('keyid', $event.target.value)"
            :placeholder="$t('Enter key')"
          />
          <template #invalid-feedback>
            <p
              v-if="
                $v.form.keyid.$dirty &&
                $v.form.keyid.containsSpecial &&
                !$v.form.keyid.minLength
              "
            >
              {{
                $t("X must be at least X characters long.", {
                  field: $t("Key"),
                  length: $v.form.keyid.$params.minLength.min,
                })
              }}
            </p>
            <p v-if="$v.form.keyid.$dirty && !$v.form.keyid.containsSpecial">
              {{
                $t(
                  "Key can only contain letters, numbers, spaces, dots and dashes."
                )
              }}
            </p>
            <p v-else-if="!$v.form.keyid.required">
              {{ $t("This is a required field.") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12" md="6" lg="3">
        <b-form-group
          :label="$t('Expires After')"
          label-for="expiresafter"
          :state="$validateState($v.form.expiresafter)"
        >
          <b-form-input
            data-test-id="modal-cloud-keys-expires-after"
            :state="$validateState($v.form.expiresafter)"
            id="expiresafter"
            @blur="setValidationInput('expiresafter', $event.target.value)"
            :placeholder="$t('Enter number')"
            type="number"
            :min="0"
          />

          <template #invalid-feedback>
            <p
              v-if="
                $v.form.expiresafter.$dirty &&
                !$v.form.expiresafter.numeric &&
                !$v.form.expiresafter.minValue
              "
            >
              {{ $t("Must be a positive integer") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
      <b-col sm="12" md="6" lg="3">
        <b-form-group :label="$t('Period')" label-for="expiresafterperiod">
          <b-form-select
            id="expiresafterperiod"
            data-test-id="modal-cloud-keys-expires-after-period"
            v-model="form.expiresafterperiod"
            :options="expiresafterperiod"
          ></b-form-select>
        </b-form-group>
      </b-col>
      <b-col sm="12" md="6" lg="3">
        <b-form-group
          :label="this.$t('Maximum Uses')"
          label-for="maxuse"
          :state="$validateState($v.form.maxuse)"
        >
          <b-form-input
            data-test-id="modal-cloud-keys-max-uses"
            :state="$validateState($v.form.maxuse)"
            id="maxuse"
            type="number"
            :min="0"
            @blur="setValidationInput('maxuse', $event.target.value)"
            :placeholder="$t('Enter number')"
          />
          <template #invalid-feedback>
            <p
              v-if="
                $v.form.maxuse.$dirty &&
                !$v.form.maxuse.numeric &&
                !$v.form.maxuse.minValue
              "
            >
              {{ $t("Must be a positive integer") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12" md="12" lg="6">
        <b-form-group :label="$t('Valid From')" label-for="validfrom">
          <b-form-datepicker
            id="validfrom"
            data-test-id="modal-cloud-keys-valid-from"
            v-model="form.validfrom"
            :date-format-options="dateFormatOptions"
            :max="form.validto"
            reset-button
            :reset-value="defaultDate"
            @input="$v.$touch()"
          />
        </b-form-group>
      </b-col>
      <b-col sm="12" md="12" lg="6">
        <b-form-group :label="$t('Valid To')" label-for="validto">
          <b-form-datepicker
            id="validto"
            data-test-id="modal-cloud-keys-valid-to"
            v-model="form.validto"
            :date-format-options="dateFormatOptions"
            :min="form.validfrom"
            reset-button
            @input="$v.$touch()"
          /> </b-form-group
      ></b-col>
      <b-col sm="12" md="12" lg="6">
        <b-form-group :label="$t('Status')" label-for="active">
          <b-form-select
            id="active"
            data-test-id="modal-cloud-keys-status"
            v-model="form.active"
            :options="status"
          ></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12">
        <b-form-group
          :label="$t('Description')"
          label-for="comment"
          :state="$validateState($v.form.comment)"
        >
          <b-form-textarea
            id="comment"
            data-test-id="modal-cloud-keys-description"
            :state="$validateState($v.form.comment)"
            @blur="setValidationInput('comment', $event.target.value)"
            no-resize
            :placeholder="$t('e.g. Head Office')"
          ></b-form-textarea>
          <template #invalid-feedback>
            <p v-if="$v.form.comment.$dirty && !$v.form.comment.minLength">
              {{
                $t("X must be at least X characters long.", {
                  field: $t("Comment"),
                  length: $v.form.comment.$params.minLength.min,
                })
              }}
            </p>
          </template>
        </b-form-group>
      </b-col>
    </b-row>
  </form>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from "vuex";
import dayjs from "dayjs";
import {
  required,
  integer,
  minLength,
  minValue,
} from "vuelidate/lib/validators";

export default {
  data() {
    return {
      form: {
        logname: null,
        keyid: null,
        expiresafter: null,
        expiresafterperiod: "hours",
        maxuse: null,
        validfrom: null,
        validto: null,
        comment: null,
        active: true,
      },
      expiresafterperiod: [
        {
          value: "days",
          text: this.$t("Days"),
        },
        {
          value: "hours",
          text: this.$t("Hours"),
        },
        {
          value: "mins",
          text: this.$t("Minutes"),
        },
      ],
      status: [
        {
          value: true,
          text: this.$t("Active"),
        },
        {
          value: false,
          text: this.$t("Inactive"),
        },
      ],
      dateFormatOptions: {
        year: "numeric",
        month: "long",
        day: "numeric",
      },
    };
  },
  validations: {
    form: {
      logname: {
        minLength: minLength(5),
        required,
      },
      keyid: {
        required,
        containsSpecial(key) {
          return this.$containsSpecial(key);
        },
        minLength: minLength(5),
      },
      expiresafter: {
        integer,
        minValue: minValue(0),
      },
      maxuse: {
        integer,
        minValue: minValue(0),
      },
      comment: {
        minLength: minLength(5),
      },
    },
  },
  methods: {
    ...mapMutations("modal", ["userAction", "showModal"]),
    ...mapActions("cloudKeys", ["createCustomerCloudKey"]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        const data = Object.fromEntries(
          Object.entries(this.form).filter(([, value]) => {
            return value !== null;
          })
        );

        this.createCustomerCloudKey({
          id: this.selectedUser.id,
          body: data,
        });

        this.showModal(false);
      }
    },
  },
  computed: {
    ...mapGetters("modal", ["modalData"]),
    ...mapGetters("authentication", ["selectedUser"]),
    defaultDate: () => dayjs().format("YYYY-MM-DD"),
  },
  created() {
    this.form.validfrom = this.defaultDate;
    this.userAction(this.submit);
  },
};
</script>
